import React from 'react';
import type { Workflow } from '@/types/workflow';

export interface WorkflowWithProvider extends Workflow {
  provider?: string;
  sourceIds?: string[];
}

interface WorkflowListProps {
  workflows: WorkflowWithProvider[];
  currentPageId: string | null;
  onWorkflowClick?: (workflowId: string) => void;
  onNewAutomationClick?: () => void;
}

function formatProvider(raw: string): string {
  const cleaned = raw.replace(/^custom\./i, '');
  return cleaned.charAt(0).toUpperCase() + cleaned.slice(1);
}

const PlusIcon: React.FC = () => (
  <svg
    width="12"
    height="12"
    viewBox="0 0 12 12"
    fill="none"
    stroke="currentColor"
    strokeWidth="2.2"
  >
    <path d="M6 1v10M1 6h10" />
  </svg>
);

const WorkflowIcon: React.FC<{ provider?: string; name: string }> = ({ provider, name }) => {
  const label = provider ? formatProvider(provider) : name;
  const initials = label
    .split(/[\s→\->/]+/)
    .filter(Boolean)
    .slice(0, 2)
    .map((word) => word[0]?.toUpperCase() ?? '')
    .join('');

  return (
    <div className="grid h-[34px] min-h-[34px] w-[34px] min-w-[34px] shrink-0 place-items-center rounded-[8px] bg-violet-100 text-[14px] font-bold text-violet-600">
      {initials || '?'}
    </div>
  );
};

const StatusPill: React.FC<{ isActive: boolean }> = ({ isActive }) => (
  <span
    className={`shrink-0 rounded-full px-2 py-[2px] text-[10px] font-medium whitespace-nowrap ${
      isActive ? 'bg-green-100 text-green-600' : 'bg-gray-100 text-gray-500'
    }`}
  >
    {isActive ? '• Active' : 'Draft'}
  </span>
);

function getDisplayName(workflow: WorkflowWithProvider): string {
  const provider = workflow.provider ? formatProvider(workflow.provider) : null;
  if (!provider) return workflow.name;
  const words = workflow.name.split(/[\s→\->/]+/).filter(Boolean);
  const firstWord = words[0] ?? workflow.name;
  if (firstWord.toLowerCase() === provider.toLowerCase()) return workflow.name;
  return `${firstWord} → ${provider}`;
}

function getScope(sourceIds: string[] | undefined, currentPageId: string | null): string {
  if (!sourceIds || sourceIds.length === 0) return 'No pages';
  if (currentPageId && sourceIds.includes(currentPageId)) return 'This page';
  const count = sourceIds.length;
  return count === 1 ? '1 page' : `${count} pages`;
}

function getMetaLine(workflow: WorkflowWithProvider, currentPageId: string | null): string | null {
  if (!workflow.provider) return null;
  const provider = formatProvider(workflow.provider);
  const scope = getScope(workflow.sourceIds, currentPageId);
  return `${provider} · ${scope}`;
}

const WorkflowCard: React.FC<{
  workflow: WorkflowWithProvider;
  currentPageId: string | null;
  onClick: () => void;
}> = ({ workflow, currentPageId, onClick }) => {
  const displayName = getDisplayName(workflow);
  const meta = getMetaLine(workflow, currentPageId);

  return (
    <button
      onClick={onClick}
      className="box-border flex w-full cursor-pointer items-center gap-[9px] overflow-hidden rounded-[8px] border border-[#E3E3E8] bg-white px-[10px] py-[9px] text-left transition-[border-color,box-shadow] duration-[120ms] outline-none hover:border-[#5B4AF8] hover:shadow-sm focus:outline-none focus-visible:outline-none"
    >
      <WorkflowIcon provider={workflow.provider} name={workflow.name} />
      <div className="min-w-0 flex-1 overflow-hidden">
        <p className="truncate text-[11.5px] font-semibold text-[#16161A]">{displayName}</p>
        {meta && <p className="mt-px truncate text-[10.5px] text-[#9898A2]">{meta}</p>}
      </div>
      <StatusPill isActive={workflow.isActive} />
    </button>
  );
};

const SectionLabel: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <p className="mb-1 text-[10.5px] font-bold tracking-[0.06em] text-[#9898A2] uppercase">
    {children}
  </p>
);

export const WorkflowList: React.FC<WorkflowListProps> = ({
  workflows,
  currentPageId,
  onWorkflowClick,
  onNewAutomationClick,
}) => {
  const activeWorkflows = workflows.filter((w) => w.isActive);
  const draftWorkflows = workflows.filter((w) => !w.isActive);

  return (
    <div className="flex w-full flex-col">
      {activeWorkflows.length > 0 && (
        <div>
          <SectionLabel>Active</SectionLabel>
          <div className="flex flex-col gap-[6px]">
            {activeWorkflows.map((workflow) => (
              <WorkflowCard
                key={workflow._meta.id}
                workflow={workflow}
                currentPageId={currentPageId}
                onClick={() => onWorkflowClick?.(workflow._meta.id)}
              />
            ))}
          </div>
        </div>
      )}

      {draftWorkflows.length > 0 && (
        <div>
          {activeWorkflows.length > 0 && <div className="h-[10px]" />}
          <SectionLabel>Drafts</SectionLabel>
          <div className="flex flex-col gap-[6px]">
            {draftWorkflows.map((workflow) => (
              <WorkflowCard
                key={workflow._meta.id}
                workflow={workflow}
                currentPageId={currentPageId}
                onClick={() => onWorkflowClick?.(workflow._meta.id)}
              />
            ))}
          </div>
        </div>
      )}

      <button
        onClick={onNewAutomationClick ?? undefined}
        className="mt-[10px] flex w-full cursor-pointer items-center justify-center gap-[7px] rounded-[8px] border-[1.5px] border-dashed border-[#C8C8D0] bg-transparent p-[10px] text-[12.5px] font-medium text-[#5B4AF8] transition-[border-color,color,background-color] duration-150 outline-none hover:border-[#5B4AF8] hover:bg-[#EEF0FF] focus:outline-none focus-visible:outline-none"
      >
        <PlusIcon />
        New automation
      </button>
    </div>
  );
};
